import { useEffect, useState, useCallback, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, RefreshCw, Loader2, Printer, Copy, Wallet, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import { getMembershipCard, regenerateMembershipQr } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase F (2026-09-21) — Detection UA UX-only. Ne sert JAMAIS pour la
// securite (l'endpoint backend revalide role + adhesion active).
function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

const TYPE_COLORS = {
  standard: { top: "bg-primary text-white", label_fr: "ADHÉRENT", label_en: "MEMBER" },
  founder: { top: "bg-amber-600 text-white", label_fr: "MEMBRE FONDATEUR", label_en: "FOUNDING MEMBER" },
  partner: { top: "bg-blue-700 text-white", label_fr: "PARTENAIRE", label_en: "PARTNER" },
  donor_partner: { top: "bg-violet-700 text-white", label_fr: "PARTENAIRE DONATEUR", label_en: "DONOR PARTNER" },
};

function _fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : null;
}

function _bilingualValidity(card) {
  const vt = card.validity_type || "annual";
  if (vt === "lifetime") return { fr: "Validité permanente", en: "Lifetime validity" };
  const from = _fmtDate(card.valid_from);
  const until = _fmtDate(card.expires_on);
  if (from && until) return { fr: `Valable du ${from} au ${until}`, en: `Valid from ${from} to ${until}` };
  if (until) return { fr: `Valable jusqu'au ${until}`, en: `Valid until ${until}` };
  if (vt === "annual") return { fr: "Date de validité à renseigner", en: "Validity date to be set" };
  return { fr: card.validity_label || "—", en: "" };
}

export default function MembershipCardDialog({ membershipId, onClose }) {
  const { t } = useTranslation();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [walletCfg, setWalletCfg] = useState({ apple_enabled: false, google_enabled: false });
  const [walletBusy, setWalletBusy] = useState(null);
  const platform = useMemo(() => detectPlatform(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMembershipCard(membershipId);
      setCard(data);
    } catch (e) {
      toast.error("Impossible de charger la carte adhérent");
    } finally {
      setLoading(false);
    }
  }, [membershipId]);

  useEffect(() => { if (membershipId) load(); }, [membershipId, load]);
  useEffect(() => {
    axios.get(`${API}/wallet/config-status`).then(r => setWalletCfg(r.data)).catch(() => {});
  }, []);

  const addToAppleWallet = async () => {
    setWalletBusy("apple");
    try {
      const r = await axios.get(`${API}/wallet/apple/${membershipId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.apple.pkpass" }));
      const link = document.createElement("a");
      link.href = url; link.download = `itercatus-${card?.public_member_id || membershipId}.pkpass`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("wallet.appleError");
      toast.error(msg);
    } finally { setWalletBusy(null); }
  };

  const addToGoogleWallet = async () => {
    setWalletBusy("google");
    try {
      const { data } = await axios.get(`${API}/wallet/google/${membershipId}`);
      if (data.save_url) window.open(data.save_url, "_blank");
      else toast.error(t("wallet.googleLinkMissing"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("wallet.googleError");
      toast.error(msg);
    } finally { setWalletBusy(null); }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Régénérer le QR ? L'ancien QR imprimé deviendra définitivement invalide.")) return;
    setRegenerating(true);
    try {
      await regenerateMembershipQr(membershipId);
      toast.success("QR régénéré — l'ancien token est désormais invalide");
      await load();
    } catch (e) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegenerating(false);
    }
  };

  const verifyUrl = card?.verification_token
    ? `${window.location.origin}/verify-member/${card.verification_token}`
    : null;

  const copyUrl = () => {
    if (!verifyUrl) return;
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Lien copié");
  };

  const handlePrint = () => window.print();

  if (!membershipId) return null;

  const style = TYPE_COLORS[card?.member_type || "standard"] || TYPE_COLORS.standard;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 print:bg-white print:p-0">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden print:shadow-none print:max-w-full" data-testid="membership-card-dialog">
        <div className="flex items-center justify-between p-4 border-b print:hidden">
          <h2 className="font-semibold text-primary">Carte ITERCATUS</h2>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-card-dialog">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          </div>
        ) : !card ? (
          <div className="p-6 text-center text-muted-foreground">Aucune donnée</div>
        ) : (
          <div className="p-6">
            {/* Carte visuelle */}
            <div className="rounded-xl overflow-hidden border shadow-sm bg-white">
              <div className={`${style.top} px-6 py-5 flex items-center gap-5`}>
                <img
                  src="/images/logo-itercatus.png"
                  alt="ITERCATUS"
                  className="h-20 w-20 object-contain drop-shadow-lg bg-white/95 rounded-lg p-1.5"
                  data-testid="card-logo"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-black text-2xl tracking-wide leading-none">ITERCATUS</div>
                  <div className="mt-2 text-sm font-semibold uppercase tracking-widest" data-testid="card-type-label">
                    {style.label_fr}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80 leading-tight" data-testid="card-type-label-en">
                    {style.label_en}
                  </div>
                </div>
              </div>
              <div className="p-5 grid grid-cols-3 gap-4 items-center">
                <div className="col-span-2 space-y-1">
                  {(card.first_name || card.last_name) ? (
                    <>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Adhérent / Member</div>
                      <div className="text-lg font-semibold text-primary" data-testid="card-name">
                        {[card.civility, card.first_name, card.last_name].filter(Boolean).join(" ")}
                      </div>
                    </>
                  ) : null}
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-3">N° Adhérent / Member ID</div>
                  <div className="font-mono text-primary text-sm" data-testid="card-number">
                    {card.public_member_id || card.member_number}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-3">Validité / Validity</div>
                  <div className={card.is_valid ? "text-emerald-700 font-medium text-sm" : "text-red-700 font-medium text-sm"} data-testid="card-validity">
                    {_bilingualValidity(card).fr}
                  </div>
                  {_bilingualValidity(card).en ? (
                    <div className="text-[10px] italic text-muted-foreground" data-testid="card-validity-en">
                      {_bilingualValidity(card).en}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col items-center gap-1">
                  {verifyUrl ? (
                    <div className="p-2 bg-white border rounded" data-testid="card-qr">
                      <QRCodeSVG value={verifyUrl} size={110} level="M" />
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">QR indisponible</div>
                  )}
                  {verifyUrl ? (
                    <div className="text-[9px] text-center text-muted-foreground leading-tight">
                      Vérifier l'adhésion<br/>Verify membership
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="px-5 py-2 border-t bg-muted/40 flex items-center justify-end text-[10px] text-muted-foreground">
                <span>itercatus.eu</span>
              </div>
            </div>

            {/* URL + actions (masqués à l'impression) */}
            <div className="mt-5 space-y-3 print:hidden">
              {verifyUrl ? (
                <div className="flex items-center gap-2 text-xs bg-muted rounded p-2 font-mono break-all" data-testid="card-verify-url">
                  <span className="flex-1">{verifyUrl}</span>
                  <button className="hover:text-primary" onClick={copyUrl} title="Copier le lien" data-testid="copy-verify-url">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}
              <div className="flex justify-between gap-2">
                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2" data-testid="print-card-btn">
                  <Printer className="h-4 w-4" /> Imprimer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="gap-2 text-red-700 hover:text-red-800"
                  data-testid="regenerate-qr-btn"
                >
                  {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Régénérer le QR
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Le token brut n'est jamais stocké en clair : le QR encode uniquement un lien de vérification.
                La régénération invalide définitivement l'ancien QR.
              </p>

              {/* Wallets Apple / Google — Phase F : ordre selon plateforme */}
              <div className="pt-3 border-t border-border space-y-2" data-testid="wallet-actions">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {t("wallet.sectionLabel")}
                </div>
                <div className={`flex gap-2 ${platform === "ios" ? "" : platform === "android" ? "flex-row-reverse" : ""}`}>
                  {walletCfg.apple_enabled ? (
                    <Button
                      variant="outline" size="sm"
                      onClick={addToAppleWallet}
                      disabled={walletBusy === "apple"}
                      className="flex-1 gap-2"
                      data-testid="apple-wallet-btn"
                    >
                      {walletBusy === "apple" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
                      {t("wallet.appleButton")}
                    </Button>
                  ) : (
                    <div
                      className="flex-1 h-9 px-3 flex items-center justify-center gap-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground"
                      data-testid="apple-wallet-disabled"
                    >
                      <Smartphone className="h-3.5 w-3.5" />
                      {t("wallet.appleSoon")}
                    </div>
                  )}
                  {walletCfg.google_enabled ? (
                    <Button
                      variant="outline" size="sm"
                      onClick={addToGoogleWallet}
                      disabled={walletBusy === "google"}
                      className="flex-1 gap-2"
                      data-testid="google-wallet-btn"
                    >
                      {walletBusy === "google" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                      {t("wallet.googleButton")}
                    </Button>
                  ) : (
                    <div
                      className="flex-1 h-9 px-3 flex items-center justify-center gap-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground"
                      data-testid="google-wallet-disabled"
                    >
                      <Wallet className="h-3.5 w-3.5" />
                      {t("wallet.googleSoon")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
